import { getReportExclusionSummary } from "../../../../service/httpsCalls";
import type { ExclusionSummary, ExclusionSummarySource } from "../types";

// Reason aggregation can be expensive. Prefetch all metrics without flooding the
// database, and remove queued work immediately when its report/tab is left.
const MAX_REASON_REQUESTS = 2;
let activeReasonRequests = 0;
const pendingReasonRequests: { key: string; start: () => void }[] = [];
const reasonRequestKey = (source: ExclusionSummarySource, metricId: string) =>
    JSON.stringify([source.reportId, source.configVersionId, source.artifactId, metricId]);

export function prioritizeExclusionReasons(source: ExclusionSummarySource, metricId: string) {
    const index = pendingReasonRequests.findIndex(request => request.key === reasonRequestKey(source, metricId));
    if (index > 0) pendingReasonRequests.unshift(pendingReasonRequests.splice(index, 1)[0]);
}

export const loadExclusionSummary = (source: ExclusionSummarySource, signal: AbortSignal, metricId?: string, page = 1, priority = false): Promise<ExclusionSummary> => {
    const request = () => getReportExclusionSummary(source.reportId, source.configVersionId, source.artifactId, metricId, page, signal);
    if (metricId == null) return request();
    return new Promise((resolve, reject) => {
        const cancel = () => {
            const index = pendingReasonRequests.findIndex(request => request.start === start);
            if (index >= 0) pendingReasonRequests.splice(index, 1);
            signal.removeEventListener("abort", cancel);
            reject(new DOMException("Reason loading was cancelled.", "AbortError"));
        };
        const start = () => {
            signal.removeEventListener("abort", cancel);
            if (signal.aborted) { cancel(); return; }
            activeReasonRequests++;
            request().then(resolve, reject).finally(() => {
                activeReasonRequests--;
                while (activeReasonRequests < MAX_REASON_REQUESTS && pendingReasonRequests.length) {
                    pendingReasonRequests.shift()!.start();
                }
            });
        };
        if (signal.aborted) { cancel(); return; }
        if (activeReasonRequests < MAX_REASON_REQUESTS) start();
        else {
            const pending = { key: reasonRequestKey(source, metricId), start };
            if (priority) pendingReasonRequests.unshift(pending);
            else pendingReasonRequests.push(pending);
            signal.addEventListener("abort", cancel, { once: true });
        }
    });
};

export function exclusionSummaryError(error: unknown): string {
    const response = (error as { response?: { status?: number; data?: unknown } })?.response;
    if (response?.status === 401) return "Your session has expired. Sign in again to view the summary.";
    if (response?.status === 403) return "You do not have permission to view this report.";
    if (response?.status === 410) return "This preview has expired. Refresh the report to view its summary.";
    const data = response?.data;
    if (typeof data === "string" && data.length < 500 && !data.startsWith("<")) return data;
    return (data as { message?: string })?.message || "Unable to load the exclusion summary. Please retry.";
}
